/**
 * NotificationDisclosure — a notification's title + body, expandable in place.
 * (W8.2 · F49 slice 3 · D-30)
 *
 * ─── What was wrong ───────────────────────────────────────────────────────
 *
 * Both web bells clamped the title to ONE line (`truncate`) and the body to
 * two, and the only way to read the rest was a hover tooltip: unavailable on
 * touch, invisible to keyboard-only navigation, and rendered inside a
 * `max-w-xs` box that clipped anything long a second time. On a production feed
 * where every notification body is rendered server-side into a full sentence,
 * that meant a meaningful share of the copy was unreachable on the devices most
 * people read it on.
 *
 * ─── The rule this component encodes ──────────────────────────────────────
 *
 * D-30 says collapsed is title 2 lines, body 3, and expanding marks the
 * notification read. But H14 is the constraint that actually shapes it: the
 * business-web notifications PAGE has no clamp at all today, so switching a
 * "collapsible cards" feature on there would ADD truncation to text that is
 * currently fully visible — a regression wearing a feature's clothes.
 *
 * So the rule is not "clamp to D-30's numbers everywhere". It is:
 *
 *     turning the flag on must never make visible text unreachable.
 *
 * On a surface that already clamps, this component raises the limits (1 → 2
 * lines of title, 2 → 3 of body) AND adds a way to see the rest: strictly more
 * text, on every device, with the hover-only escape hatch replaced by something
 * a thumb and a keyboard can both reach. On a surface that clamps nothing, the
 * caller does not mount it — there is nothing to disclose, and mounting it
 * would be the regression H14 names. `bodyLines={null}` expresses that
 * explicitly for a caller that wants the title treatment without the body one.
 *
 * ─── The toggle is its own target ─────────────────────────────────────────
 *
 * Opening the notification is the row's own button, stretched over the row;
 * "Show more" sits ABOVE it (`relative z-10`) as a separate, focusable
 * sibling — never nested inside it — so expanding never navigates away.
 * `aria-expanded` and `aria-controls` make the state audible to a screen
 * reader rather than leaving it as a visual-only affordance.
 *
 * Presentation only. It owns no cache, no mutation and no flag: the caller
 * decides when to mount it, and `onExpand` is where "expanding marks it read"
 * is wired, because marking read is a data concern and this file is not.
 */

import * as React from 'react';

import { cn } from '../lib/utils';

export interface NotificationDisclosureProps {
  title: string;
  body: string | null | undefined;
  /** Collapsed title clamp. D-30 says two. */
  titleLines?: 1 | 2 | 3;
  /**
   * Collapsed body clamp, or `null` for "do not clamp the body at all".
   *
   * `null` is the H14 escape: a surface whose body is unclamped today must not
   * gain a clamp, because the feature would then be removing text.
   */
  bodyLines?: 2 | 3 | 4 | null;
  /** Emphasis for an unread row, decided by the caller. */
  titleClassName?: string;
  bodyClassName?: string;
  /** Accessible label for the control while collapsed. */
  expandLabel: string;
  /** …and while expanded. */
  collapseLabel: string;
  /**
   * Called the FIRST time the reader expands this notification, and never on
   * collapse. Where "expanding marks it read" is wired.
   */
  onExpand?: () => void;
  /** Stable id fragment for the testids and for `aria-controls`. */
  domId: string;
}

const TITLE_CLAMP: Record<number, string> = {
  1: 'line-clamp-1',
  2: 'line-clamp-2',
  3: 'line-clamp-3',
};

const BODY_CLAMP: Record<number, string> = {
  2: 'line-clamp-2',
  3: 'line-clamp-3',
  4: 'line-clamp-4',
};

export function NotificationDisclosure({
  title,
  body,
  titleLines = 2,
  bodyLines = 3,
  titleClassName,
  bodyClassName,
  expandLabel,
  collapseLabel,
  onExpand,
  domId,
}: NotificationDisclosureProps) {
  const [expanded, setExpanded] = React.useState(false);
  const bodyId = `notification-body-${domId}`;

  const toggle = () => {
    setExpanded((wasExpanded) => {
      if (!wasExpanded) onExpand?.();
      return !wasExpanded;
    });
  };

  return (
    <>
      <p
        className={cn('text-sm', !expanded && TITLE_CLAMP[titleLines], titleClassName)}
        data-testid={`notification-title-${domId}`}
        data-expanded={expanded}
      >
        {title}
      </p>
      {body ? (
        <p
          id={bodyId}
          className={cn(
            'mt-0.5 text-xs text-muted-foreground',
            // `bodyLines === null` is a deliberate "never clamp" — see H14 in
            // the header. It is not a missing default.
            !expanded && bodyLines !== null && BODY_CLAMP[bodyLines],
            bodyClassName
          )}
          data-testid={`notification-body-${domId}`}
          data-expanded={expanded}
        >
          {body}
        </p>
      ) : null}
      <button
        type="button"
        onClick={toggle}
        aria-expanded={expanded}
        aria-controls={body ? bodyId : undefined}
        aria-label={expanded ? collapseLabel : expandLabel}
        data-testid={`notification-disclosure-${domId}`}
        className="relative z-10 mt-1 text-2xs font-medium text-primary-accessible underline-offset-2 outline-none hover:underline focus-visible:underline"
      >
        {expanded ? collapseLabel : expandLabel}
      </button>
    </>
  );
}
